import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";

import { getReportDetails, previewReportCancelToken } from "../../services/reportService";
import SkeletonReportPreview from "../../loaders/SkeletonReportPreview";
import {
  canAccessReport,
  getLoggedInReportUserContext,
} from "./reportAccessUtils";

/**
 * Full-page Power BI report preview for a saved report id.
 * @returns {JSX.Element} Embedded Power BI report view.
 */
const ReportView = () => {
  const history = useHistory();
  const { id } = useParams();
  const givenPermissions = useSelector((state) => state.givenPermission);
  const loggedInUser = useSelector((state) => state.user);
  const { userId, roleId } = getLoggedInReportUserContext(loggedInUser);
  const [loading, setLoading] = useState(false);
  const [powerBIConfig, setPowerBIConfig] = useState(null);

  useEffect(() => {
    const cancelTokenSource = axios.CancelToken.source();

    const handlePreviewReport = async () => {
      setLoading(true);

      try {
        const reportRes = await getReportDetails({ id }, cancelTokenSource.token);

        if (reportRes.data?.status) {
          const report = reportRes.data.data;

          if (
            !canAccessReport({
              report,
              givenPermissions,
              userId,
              roleId,
              action: "view",
            })
          ) {
            history.push("/noaccess");
            return;
          }
        }

        const res = await previewReportCancelToken(
          { id },
          cancelTokenSource.token
        );

        if (res.status === 200 || res.data?.status) {
          const data = res.data?.data || res.data;
          setPowerBIConfig({
            type: "report",
            id: data.reportId,
            embedUrl: data.embedUrl,
            accessToken: data.embedToken,
            tokenType: models.TokenType.Embed,
            settings: {
              panes: {
                filters: {
                  expanded: false,
                  visible: false,
                },
              },
              navContentPaneEnabled: true,
            },
          });
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          setPowerBIConfig(null);
        }
      } finally {
        setLoading(false);
      }
    };

    handlePreviewReport();

    return () => {
      cancelTokenSource.cancel("ReportView unmounted or report id changed");
    };
  }, [id, givenPermissions, userId, roleId, history]);

  return (
    <div className="card card-body-inr powerbi-report-view">
      <div>
        {!loading && powerBIConfig ? (
          <PowerBIEmbed
            embedConfig={powerBIConfig}
            cssClassName="powerbi-container"
          />
        ) : (
          <SkeletonReportPreview />
        )}
      </div>
    </div>
  );
};

export default ReportView;
